import { useState, type FormEvent } from 'react';
import { X, Users, AlertCircle } from 'lucide-react';
import { createCustomer, updateCustomer } from '../utils/customerApi';
import { ApiValidationError } from '../types/auth';
import type { Customer, CustomerType, PaymentTerms } from '../types/customer';
import {
  CUSTOMER_TYPES,
  PAYMENT_TERMS,
  customerToFormData,
  defaultCustomerFormData,
  formatCustomerLabel,
  toCreateCustomerPayload,
  toUpdateCustomerPayload,
  validateCustomerForm,
  type CustomerFormData,
} from '../utils/customerForm';

export interface CustomerFormModalProps {
  customer?: Customer | null;
  onClose: () => void;
  onSaved: (customer: Customer) => void;
  initialName?: string;
  subtitle?: string;
}

const CustomerFormModal = ({
  customer,
  onClose,
  onSaved,
  initialName = '',
  subtitle,
}: CustomerFormModalProps) => {
  const isEdit = Boolean(customer);
  const [formData, setFormData] = useState<CustomerFormData>(() =>
    customer ? customerToFormData(customer) : defaultCustomerFormData({ name: initialName })
  );
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setModalError(null);
    setFieldErrors({});

    const validationErrors = validateCustomerForm(formData);
    if (validationErrors) {
      setFieldErrors(validationErrors);
      setModalError('Please fix the highlighted fields.');
      return;
    }

    try {
      setSubmitting(true);
      const saved = customer
        ? await updateCustomer(customer.id, toUpdateCustomerPayload(formData))
        : await createCustomer(toCreateCustomerPayload(formData));
      onSaved(saved);
      onClose();
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        setModalError(err instanceof Error ? err.message : 'Failed to save customer');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-slate-700" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEdit ? 'Edit Customer' : 'Create Customer'}
              </h3>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {modalError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{modalError}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                maxLength={200}
                value={formData.display_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, display_name: e.target.value }))
                }
                className={`w-full border rounded-lg px-3.5 py-2.5 text-sm ${
                  fieldError('display_name') ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="John Doe Corp"
              />
              {fieldError('display_name') && (
                <p className="mt-1 text-sm text-red-600">{fieldError('display_name')}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  maxLength={150}
                  value={formData.email ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className={`w-full border rounded-lg px-3.5 py-2.5 text-sm ${
                    fieldError('email') ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {fieldError('email') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('email')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  maxLength={30}
                  value={formData.phone ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  className={`w-full border rounded-lg px-3.5 py-2.5 text-sm ${
                    fieldError('phone') ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="+94771234567"
                />
                {fieldError('phone') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('phone')}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea
                rows={2}
                value={formData.address ?? ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                placeholder="123 Main St"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Type</label>
                <select
                  value={formData.customer_type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      customer_type: e.target.value as CustomerType,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white"
                >
                  {CUSTOMER_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {formatCustomerLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                <select
                  value={formData.payment_terms}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      payment_terms: e.target.value as PaymentTerms,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white"
                >
                  {PAYMENT_TERMS.map((term) => (
                    <option key={term} value={term}>
                      {formatCustomerLabel(term)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.active ?? true}
                onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              Active customer
            </label>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm text-white bg-slate-800 disabled:opacity-60"
            >
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerFormModal;